import { useEffect, useRef, useState } from "react";

/** How long a count to a new value takes (ms): quickly, then slower. */
export const COUNT_MS = 700;

/**
 * A number that counts to each new value over `ms`, quickly then slower; at once under reduced
 * motion. It runs only while counting, never at rest. `rise`: only a rise counts, a fall shows at
 * once (a balance: money added rolls up, money spent never rolls down).
 */
export function useCountTo(
  target: number,
  ms = COUNT_MS,
  from?: number,
  counts: "both" | "rise" = "both",
): number {
  const [shown, setShown] = useState(from ?? target);
  const current = useRef(from ?? target);
  useEffect(() => {
    const start = current.current;
    const still =
      typeof window === "undefined" ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (still || start === target || (counts === "rise" && target < start)) {
      current.current = target;
      setShown(target);
      return;
    }
    let frame = 0;
    const began = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, Math.max(0, (now - began) / ms));
      current.current = Math.round(start + (target - start) * (1 - (1 - t) ** 3));
      setShown(current.current);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, ms, counts]);
  return shown;
}
